import { FolderOpenOutlined, RobotOutlined } from "@ant-design/icons";
import {
  Api,
  canManageContentActivation,
  CHAT_LESSON_TOPIC_OPTIONS,
  hasPermission,
  ICharacter,
  IChatLesson,
  IJourney,
  IWordCard,
  IJourneyStep,
  PERMISSIONS,
} from "@model";
import {
  getConfiguredLessonLanguageKeys,
  getLessonListTitle,
  legacyLanguageKey,
  voiceCodeForLanguageKey,
} from "../ChatLesson/chatLessonLanguages";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tabs,
  Tooltip,
  message,
} from "antd";
import {
  AdminFileManager,
  buildMediaFullUrl,
  StickyFormActions,
} from "@components";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import JourneyStepsFlow, {
  defaultJourneyStep,
  newStepId,
} from "./JourneyStepsFlow";
import JourneyLogsModal from "./JourneyLogsModal";
import {
  JOURNEY_LANGUAGES,
  cleanJourneyForSave,
  normalizeJourneyForForm,
  parseJourneyJson,
} from "./journeyLanguages";

const METADATA_SECTION_STYLE: CSSProperties = {
  background: "#f1f1f1",
  border: "1px solid #f0f0f0",
  borderRadius: 8,
  padding: "12px 16px 4px",
  marginBottom: 16,
};

const METADATA_SECTION_LABEL_STYLE: CSSProperties = {
  marginBottom: 10,
  fontSize: 12,
  fontWeight: 600,
  color: "rgba(0, 0, 0, 0.55)",
  letterSpacing: 0.3,
};

type MetadataTranslation = Record<
  string,
  { title?: string; description?: string; starterText?: string }
>;

function mergeMetadataTranslations<
  T extends {
    title?: string;
    description?: string;
    starterText?: string;
  },
>(current: Record<string, T> | undefined, translations: MetadataTranslation) {
  const next = { ...(current ?? {}) };
  for (const [langKey, fields] of Object.entries(translations)) {
    if (langKey === "en" || !fields) continue;
    next[langKey] = {
      ...next[langKey],
      ...(fields.title?.trim() ? { title: fields.title.trim() } : {}),
      ...(fields.description?.trim()
        ? { description: fields.description.trim() }
        : {}),
      ...(fields.starterText?.trim()
        ? { starterText: fields.starterText.trim() }
        : {}),
    };
  }
  return next;
}

function renderMetadataTranslateButton(options: {
  loading: boolean;
  onClick: () => void;
  tooltip: string;
}) {
  return (
    <Tooltip title={options.tooltip}>
      <Button
        type="text"
        aria-label={options.tooltip}
        icon={<RobotOutlined />}
        loading={options.loading}
        onClick={options.onClick}
      />
    </Tooltip>
  );
}

const toStoredMediaPath = (fullUrl: string): string => {
  try {
    return new URL(fullUrl).pathname.replace(/^\//, "");
  } catch {
    return fullUrl.replace(/^https?:\/\/[^/]+\//, "").replace(/^\//, "");
  }
};

const mediaPreviewSrc = (path?: string) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return buildMediaFullUrl(path);
};

const defaultStep = defaultJourneyStep;

const sortAndNormalizeSubSteps = (
  subSteps: IJourneyStep[] = [],
): IJourneyStep[] =>
  [...subSteps]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((sub, index) => normalizeJourneyStep(sub, index, true));

const normalizeJourneyStep = (
  step: IJourneyStep,
  index: number,
  isSubStep = false,
): IJourneyStep => {
  const subSteps = step.subSteps?.length
    ? sortAndNormalizeSubSteps(step.subSteps)
    : undefined;

  return {
    ...step,
    stepId: step.stepId || newStepId(),
    order: index,
    isOptional: isSubStep ? false : (step.isOptional ?? false),
    xpReward: step.xpReward ?? 0,
    subSteps,
  };
};

const normalizeSteps = (steps: IJourneyStep[] = []): IJourneyStep[] =>
  steps.map((step, index) => normalizeJourneyStep(step, index));

const JourneyCreatePage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm<IJourney>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [loadingStepOptions, setLoadingStepOptions] = useState(false);
  const [lessons, setLessons] = useState<IChatLesson[]>([]);
  const [characters, setCharacters] = useState<ICharacter[]>([]);
  const [wordCards, setWordCards] = useState<IWordCard[]>([]);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [startVideoModalOpen, setStartVideoModalOpen] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importJsonText, setImportJsonText] = useState("");
  const [stepMediaFileManagerPath, setStepMediaFileManagerPath] = useState<
    (string | number)[] | null
  >(null);
  const [activeLanguageTab, setActiveLanguageTab] = useState("en");
  const [resourceLanguage, setResourceLanguage] = useState(
    voiceCodeForLanguageKey("en"),
  );
  const [translatingJourneyMetadata, setTranslatingJourneyMetadata] =
    useState(false);
  const journeyPhotoMd = Form.useWatch(["photo", "md"], form) as
    | string
    | undefined;
  const journeyPhotoSm = Form.useWatch(["photo", "sm"], form) as
    | string
    | undefined;
  const journeyPhotoPreview = journeyPhotoMd || journeyPhotoSm;
  const startVideoUrl = Form.useWatch("startVideoUrl", form) as
    | string
    | undefined;
  const isMounted = useRef(true);

  const isCreateMode = !id;
  const canSave = isCreateMode
    ? hasPermission(PERMISSIONS.journeysCreate)
    : hasPermission(PERMISSIONS.journeysEdit);
  const canViewLogs = hasPermission(PERMISSIONS.journeysLogs);
  const canManageActivation = canManageContentActivation();
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ??
    "/panel/journeys";

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const lessonOptions = useMemo(
    () =>
      lessons
        .filter((l) => {
          const resourceLangKey = legacyLanguageKey(resourceLanguage);
          return getConfiguredLessonLanguageKeys(l.metadata).includes(
            resourceLangKey,
          );
        })
        .map((l) => ({
          value: l._id,
          label: getLessonListTitle(l),
        })),
    [lessons, resourceLanguage],
  );

  const characterOptions = useMemo(
    () =>
      characters.map((c) => ({
        value: c._id,
        label: c.name,
      })),
    [characters],
  );

  const wordCardOptions = useMemo(
    () =>
      wordCards.map((card) => ({
        value: card._id ?? "",
        label: [card.title, card.isActive ? "" : "(inactive)"]
          .filter(Boolean)
          .join(" · "),
      })),
    [wordCards],
  );

  const loadStepOptions = useCallback(async () => {
    setLoadingStepOptions(true);
    try {
      const [lessonsRes, charactersRes, wordCardsRes] = await Promise.all([
        Api.get("/api/admin/panel/chat-lessons", {
          params: {
            limit: 500,
            language: resourceLanguage,
            fields: "options",
          },
        }),
        Api.get("/api/admin/panel/characters", {
          params: { limit: 500, fields: "options" },
        }),
        Api.get("/api/admin/panel/word-cards", {
          params: { limit: 500, fields: "options" },
        }),
      ]);
      if (isMounted.current) {
        setLessons(lessonsRes.data.data ?? []);
        setCharacters(charactersRes.data.data ?? []);
        setWordCards(wordCardsRes.data.data ?? []);
      }
    } catch {
      if (isMounted.current) {
        message.warning(
          "Could not load lessons, characters, or word cards",
        );
        setLessons([]);
        setCharacters([]);
        setWordCards([]);
      }
    } finally {
      if (isMounted.current) setLoadingStepOptions(false);
    }
  }, [resourceLanguage]);

  useEffect(() => {
    void loadStepOptions();
  }, [loadStepOptions]);

  const resourceLanguageLabel =
    JOURNEY_LANGUAGES.find(
      (lang) => voiceCodeForLanguageKey(lang.key) === resourceLanguage,
    )?.label ?? resourceLanguage;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Api.get(`/api/admin/panel/journeys/${id}`)
      .then((response) => {
        const journey = normalizeJourneyForForm(
          response.data.data as IJourney & {
            description?: string;
            language?: string;
          },
        );
        const steps = normalizeSteps(
          [...(journey.steps ?? [])].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0),
          ),
        );
        form.setFieldsValue({ ...journey, steps });
      })
      .catch(() => message.error("Failed to load journey"))
      .finally(() => setLoading(false));
  }, [id, form]);

  const requestMetadataTranslations = async (payload: {
    title: string;
    description?: string;
    starterText?: string;
  }) => {
    const response = await Api.post(
      "/api/admin/panel/journeys/translate-metadata",
      payload,
    );
    return response.data?.data as MetadataTranslation | undefined;
  };

  const handleTranslateJourneyMetadata = async () => {
    const formValues = form.getFieldsValue(true);
    const metadata = (formValues.metadata ?? {}) as IJourney["metadata"];
    const enMeta = metadata?.en ?? {};
    const enTitle = String(enMeta.title ?? "").trim();
    const enDescription = String(enMeta.description ?? "").trim();

    if (!enTitle) {
      message.error("Fill in the English app title first");
      return;
    }

    setTranslatingJourneyMetadata(true);
    try {
      const translations = await requestMetadataTranslations({
        title: enTitle,
        ...(enDescription ? { description: enDescription } : {}),
      });
      if (!translations || !isMounted.current) {
        message.error("AI returned no translations");
        return;
      }

      form.setFieldsValue({
        metadata: mergeMetadataTranslations(metadata, translations),
      });
      message.success("Translated journey metadata from English");
    } catch {
      if (isMounted.current) message.error("Failed to translate metadata");
    } finally {
      if (isMounted.current) setTranslatingJourneyMetadata(false);
    }
  };

  const handleImportJson = () => {
    let imported: IJourney;
    try {
      imported = parseJourneyJson(importJsonText, {
        regenerateStepIds: isCreateMode,
      });
    } catch (error) {
      message.error(
        error instanceof Error && error.message.startsWith("JSON must")
          ? error.message
          : "Invalid JSON",
      );
      return;
    }

    const steps = normalizeSteps(
      [...(imported.steps ?? [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    );
    form.resetFields();
    form.setFieldsValue({ ...imported, steps });
    setImportModalOpen(false);
    setImportJsonText("");
    message.success("Journey imported. Review it, then save.");
  };

  const createOrUpdateJourney = async (_values: IJourney) => {
    setSaving(true);
    try {
      const values = form.getFieldsValue(true) as IJourney;
      const photoMd = values.photo?.md?.trim();
      const photoSm = values.photo?.sm?.trim();
      const photoPath = photoMd || photoSm;
      const payload = cleanJourneyForSave({
        ...values,
        photo: photoPath
          ? { md: photoMd || photoPath, sm: photoSm || photoPath }
          : undefined,
        startVideoUrl: values.startVideoUrl?.trim() || undefined,
        steps: normalizeSteps(values.steps),
      });
      if (!canManageActivation) {
        delete (payload as Partial<IJourney>).isActive;
      }

      if (isCreateMode) {
        await Api.post("/api/admin/panel/journeys", payload);
        message.success("Journey created successfully");
      } else {
        await Api.put(`/api/admin/panel/journeys/${id}`, payload);
        message.success("Journey updated successfully");
      }
      navigate(returnTo);
    } catch {
      message.error(
        isCreateMode ? "Failed to create journey" : "Failed to update journey",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card loading={loading}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          isActive: false,
          isPremium: false,
          steps: [],
        }}
        onFinish={createOrUpdateJourney}
      >
        <StickyFormActions
          onBack={() => navigate(returnTo)}
          backLabel="Back"
          actions={
            <Space>
              {canSave && <Button size="large" onClick={() => setImportModalOpen(true)}>
                Import from JSON
              </Button>}
              {!isCreateMode && canViewLogs && (
                <Button size="large" onClick={() => setLogsModalOpen(true)}>
                  Logs
                </Button>
              )}
              {canSave && <Button
                loading={saving}
                htmlType="submit"
                type="primary"
                size="large"
              >
                {isCreateMode ? "Create" : "Save"}
              </Button>}
            </Space>
          }
        />

        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr auto auto auto auto",
              gap: 16,
              alignItems: "start",
            }}
          >
            <Form.Item
              name="title"
              label="List title"
              tooltip="Shown in admin lists only."
              rules={[{ required: true, message: "List title is required" }]}
            >
              <Input placeholder="e.g. Travel journey" />
            </Form.Item>

            <Form.Item
              name="topic"
              label="Topic"
              rules={[{ required: true, message: "Topic is required" }]}
            >
              <Select
                mode="multiple"
                options={CHAT_LESSON_TOPIC_OPTIONS}
                placeholder="Select topics"
              />
            </Form.Item>

            <Form.Item
              name="minAgeSupport"
              label="Min age"
              tooltip="Users younger than this age won't see this journey."
            >
              <InputNumber min={0} max={120} placeholder="Any age" />
            </Form.Item>

            <Form.Item name="isActive" valuePropName="checked" label=" ">
              <Checkbox disabled={!canManageActivation}>Active</Checkbox>
            </Form.Item>

            <Form.Item name="isPremium" valuePropName="checked" label=" ">
              <Checkbox>Premium</Checkbox>
            </Form.Item>

          </div>
        </div>

        <div style={METADATA_SECTION_STYLE}>
          <div style={METADATA_SECTION_LABEL_STYLE}>
            App metadata (shown to learners per language)
          </div>
          <Tabs
            activeKey={activeLanguageTab}
            onChange={setActiveLanguageTab}
            tabBarExtraContent={renderMetadataTranslateButton({
              loading: translatingJourneyMetadata,
              onClick: handleTranslateJourneyMetadata,
              tooltip:
                "Translate English title and description to es, fr, de, it, tr, ja, ko, zh",
            })}
            items={JOURNEY_LANGUAGES.map((lang) => ({
              key: lang.key,
              label: lang.label,
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name={["metadata", lang.key, "title"]}
                    label="App title"
                    rules={[
                      {
                        required: lang.key === "en",
                        message: "Please enter an app title",
                      },
                    ]}
                  >
                    <Input placeholder={`App title in ${lang.label}`} />
                  </Form.Item>
                  <Form.Item
                    name={["metadata", lang.key, "description"]}
                    label="Description"
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder={`Description in ${lang.label}`}
                    />
                  </Form.Item>
                </>
              ),
            }))}
          />
        </div>

        <Form.Item name={["photo", "md"]} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={["photo", "sm"]} hidden>
          <Input />
        </Form.Item>

        <Form.Item label="Photo">
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: 8,
                border: "1px dashed #d9d9d9",
                background: "#fafafa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {journeyPhotoPreview ? (
                <img
                  src={mediaPreviewSrc(journeyPhotoPreview)}
                  alt="Journey"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <span style={{ color: "#bfbfbf", fontSize: 12 }}>No photo</span>
              )}
            </div>
            <Space direction="vertical" size={8}>
              <Space wrap>
                <Button
                  icon={<FolderOpenOutlined />}
                  onClick={() => setPhotoModalOpen(true)}
                >
                  Select from file manager
                </Button>
                {journeyPhotoPreview && (
                  <Button
                    danger
                    type="link"
                    onClick={() => form.setFieldsValue({ photo: undefined })}
                  >
                    Clear
                  </Button>
                )}
              </Space>
              <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                Shown on the home journey card in the app
              </span>
            </Space>
          </div>
        </Form.Item>

        <Form.Item name="startVideoUrl" hidden>
          <Input />
        </Form.Item>

        <Form.Item label="Start video">
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: 160,
                height: 90,
                borderRadius: 8,
                border: "1px dashed #d9d9d9",
                background: "#000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {startVideoUrl ? (
                <video
                  key={startVideoUrl}
                  src={mediaPreviewSrc(startVideoUrl)}
                  controls
                  preload="metadata"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span style={{ color: "#bfbfbf", fontSize: 12 }}>No video</span>
              )}
            </div>
            <Space direction="vertical" size={8}>
              <Space wrap>
                <Button
                  icon={<FolderOpenOutlined />}
                  onClick={() => setStartVideoModalOpen(true)}
                >
                  Select from file manager
                </Button>
                {startVideoUrl && (
                  <Button
                    danger
                    type="link"
                    onClick={() =>
                      form.setFieldValue("startVideoUrl", undefined)
                    }
                  >
                    Clear
                  </Button>
                )}
              </Space>
              <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                Played once in the app right after the user first picks this
                journey, before landing on home
              </span>
            </Space>
          </div>
        </Form.Item>

        <Form.List name="steps">
          {(fields, { add, remove, move }) => (
            <JourneyStepsFlow
              form={form}
              fields={fields}
              move={move}
              remove={remove}
              onAdd={() => add(defaultStep(fields.length))}
              resourceLanguage={resourceLanguage}
              resourceLanguageLabel={resourceLanguageLabel}
              onResourceLanguageChange={setResourceLanguage}
              loadingStepOptions={loadingStepOptions}
              lessonOptions={lessonOptions}
              onRefreshLessons={loadStepOptions}
              wordCardOptions={wordCardOptions}
              characterOptions={characterOptions}
              requestMetadataTranslations={requestMetadataTranslations}
              onOpenStepMediaFileManager={(path) =>
                setStepMediaFileManagerPath(path)
              }
              journeyId={id}
            />
          )}
        </Form.List>
        <div style={{ height: 400 }} aria-hidden />
      </Form>

      <Modal
        title="File manager"
        open={photoModalOpen}
        onCancel={() => setPhotoModalOpen(false)}
        footer={null}
        width="min(1320px, 96vw)"
        style={{ top: 24 }}
        bodyStyle={{ maxHeight: "calc(100vh - 180px)", overflow: "auto" }}
        destroyOnClose
      >
        <AdminFileManager
          defaultFolder="images"
          onSelectUrl={(fullUrl) => {
            const relative = toStoredMediaPath(fullUrl);
            form.setFieldValue("photo", { md: relative, sm: relative });
            setPhotoModalOpen(false);
            message.success("Photo selected");
          }}
        />
      </Modal>

      <Modal
        title="File manager — step media"
        open={stepMediaFileManagerPath != null}
        onCancel={() => setStepMediaFileManagerPath(null)}
        footer={null}
        width="min(1320px, 96vw)"
        style={{ top: 24 }}
        bodyStyle={{ maxHeight: "calc(100vh - 180px)", overflow: "auto" }}
        destroyOnClose
      >
        <AdminFileManager
          defaultFolder="images"
          onSelectUrl={(fullUrl) => {
            if (!stepMediaFileManagerPath) return;
            const relative = toStoredMediaPath(fullUrl);
            form.setFieldValue([...stepMediaFileManagerPath, "mediaFile"], {
              md: relative,
              sm: relative,
            });
            setStepMediaFileManagerPath(null);
            message.success("Media file selected");
          }}
        />
      </Modal>

      <Modal
        title="File manager — start video"
        open={startVideoModalOpen}
        onCancel={() => setStartVideoModalOpen(false)}
        footer={null}
        width="min(1320px, 96vw)"
        style={{ top: 24 }}
        bodyStyle={{ maxHeight: "calc(100vh - 180px)", overflow: "auto" }}
        destroyOnClose
      >
        <AdminFileManager
          defaultFolder="videos"
          onSelectUrl={(fullUrl) => {
            const relative = toStoredMediaPath(fullUrl);
            form.setFieldValue("startVideoUrl", relative);
            setStartVideoModalOpen(false);
            message.success("Video selected");
          }}
        />
      </Modal>

      <Modal
        title="Import journey from JSON"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={handleImportJson}
        okText="Import"
        okButtonProps={{ disabled: !importJsonText.trim() }}
        width={720}
        destroyOnClose
      >
        <p style={{ color: "#8c8c8c" }}>
          Paste a journey document copied from mongo. This replaces every field
          in the form; nothing is saved until you press{" "}
          {isCreateMode ? "Create" : "Save"}.
        </p>
        <Input.TextArea
          value={importJsonText}
          onChange={(e) => setImportJsonText(e.target.value)}
          rows={16}
          spellCheck={false}
          style={{ fontFamily: "monospace" }}
          placeholder='{ "title": "...", "topic": ["travel"], "metadata": { "en": { "title": "..." } }, "steps": [] }'
        />
      </Modal>

      {id && (
        <JourneyLogsModal
          journeyId={id}
          open={logsModalOpen}
          onClose={() => setLogsModalOpen(false)}
        />
      )}
    </Card>
  );
};

export default JourneyCreatePage;
